import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import { Chip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import {
  booleanFilterOperators,
  filterOperators,
  singleSelectFilterOperators
} from 'components/enhanced/data-grid/utils/filters';
import TenantPage from 'layouts/TenantPage';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IMovementReason } from '../types';
import { movementReasonService } from '../../api/movement-reason.service';
import { booleanCell } from 'components/cells/BooleanCell';
import { AddButton } from 'components/ui';

export const MovementReasons = () => {
  const translationPrefix = 'pages.reason.list';
  const { t } = useTranslation();
  const datagridRefresh = useRef(null);

  const columns: GridColDef[] = [
    {
      field: 'code',
      headerName: t(`global.code`),
      filterable: true,
      filterOperators,
      sortable: true,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const movementReason = params.row;
        return (
          <div>
            <Link to={`${movementReason.id}`}>
              <b className="text-secondary-500">{movementReason.code}</b>
            </Link>
          </div>
        );
      }
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      filterable: true,
      filterOperators,
      sortable: true,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const description = params.row.description;
        const parts = description ? description.split(' - ') : [];
        const label = parts.length > 1 ? parts[1] : description;

        return <div>{label}</div>;
      }
    },
    {
      field: 'direction',
      headerName: t(`entities.movement.direction`),
      filterable: true,
      sortable: true,
      type: 'singleSelect',
      valueOptions: ['IN', 'OUT'],
      flex: 1,
      filterOperators: singleSelectFilterOperators,
      renderCell(params) {
        const reason = params.row as IMovementReason;
        return reason.direction.toLocaleLowerCase() === 'in' ? (
          <Chip label={<ArrowDownwardIcon />} variant="outlined" color="success" />
        ) : (
          <Chip label={<ArrowUpward />} variant="outlined" color="error" />
        );
      }
    },
    {
      field: 'discounted',
      headerName: t(`entities.movement.discounted`),
      flex: 1,
      renderCell: booleanCell(t),
      filterOperators: booleanFilterOperators
    },
    {
      field: 'order_independent',
      headerName: t(`entities.movement.order_independent`),
      flex: 1,
      renderCell: booleanCell(t),
      filterOperators: booleanFilterOperators
    },
    {
      field: 'ddt',
      headerName: t(`entities.movement.ddt`),
      flex: 1,
      renderCell: booleanCell(t),
      filterOperators: booleanFilterOperators
    },
    {
      field: 'ddt_active',
      headerName: t(`entities.movement.ddt_active`),
      flex: 1,
      renderCell: booleanCell(t),
      filterOperators: booleanFilterOperators
    },
    {
      field: 'ddt_active_supplier',
      headerName: t(`entities.movement.ddt_active_supplier`),
      filterable: true,
      filterOperators: booleanFilterOperators,
      sortable: true,
      flex: 1,
      renderCell: booleanCell(t)
    }
  ];

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={<AddButton to="new" prefix={translationPrefix} requireTenantAdmin />}>
      <EnhancedDataGrid
        columns={columns}
        service={movementReasonService}
        refresh={datagridRefresh}
      />
    </TenantPage>
  );
};

export default MovementReasons;
